import { MouseEvent, useState, useRef } from "react";
import clsx from "clsx";
import { twMerge } from "tailwind-merge";

import { DropdownItemModel } from "@interfaces/models";
import { useClickOutside } from "@hooks/index";
import {
  ChevronDownSvg,
  ChevronSmallDownSvg,
  TrashXSvg,
} from "@components/index";

export const Dropdown = ({
  placeholder,
  label,
  isSimpleLabel = false,
  noItemsLabel,
  height = "medium",
  classNameSelected = "",
  classNameExpandedContainer = "",
  width,
  disabled,
  preText,
  textAlignLeft,
  chevronType = "default",
  showClearButton,
  expandToTop,
  items,
  value = "",
  onSelect,
  error,
  dataTestid,
}: {
  placeholder?: string;
  label?: string;
  isSimpleLabel?: boolean;
  noItemsLabel?: string;
  height?: "small" | "medium";
  classNameSelected?: string;
  classNameExpandedContainer?: string;
  width?: string;
  disabled?: boolean;
  preText?: string;
  textAlignLeft?: boolean;
  chevronType?: "default" | "smallBlue";
  showClearButton?: boolean;
  expandToTop?: boolean;
  items: DropdownItemModel[];
  value?: string;
  onSelect?: (id: string) => void;
  error?: string;
  dataTestid?: string;
}) => {
  const [isExpand, setIsExpand] = useState(false);
  const dropdownRef = useRef(null);
  useClickOutside(dropdownRef, () => setIsExpand(false));

  let heightStyles;
  switch (height) {
    case "small":
      heightStyles = "h-12 py-3";
      break;
    case "medium":
    default:
      heightStyles = "h-14 py-4";
      break;
  }

  const handleSelect = (id: string) => {
    setIsExpand(false);
    if (onSelect) {
      onSelect(id);
    }
  };

  const handleClearValue = (event: MouseEvent) => {
    event.stopPropagation();
    if (onSelect) {
      onSelect("");
    }
  };

  return (
    <div
      ref={dropdownRef}
      className={clsx("relative", width)}
      data-testid={dataTestid}
    >
      {isSimpleLabel && label && (
        <div className="font-bold text-black-70">{label}</div>
      )}
      <button
        className={twMerge(
          clsx(
            "flex w-full items-center justify-between border border-black-16 bg-white-100 px-4 hover:border-black-32 disabled:border-black-16 disabled:bg-black-4",
            heightStyles,
            value ? "text-black-100" : "text-black-54",
            isExpand && !expandToTop
              ? "rounded-t-lg"
              : isExpand && expandToTop
                ? "rounded-b-lg"
                : "rounded-lg",
            error && "border-red-100 text-red-100",
          ),
        )}
        type="button"
        onClick={() => setIsExpand(!isExpand)}
        disabled={disabled}
      >
        {!isSimpleLabel && label && (
          <div
            className={clsx(
              "absolute -top-2 -ml-1 bg-white-100 px-1 text-100 text-black-54 duration-300",
              !placeholder &&
                !value &&
                `${height === "medium" ? "top-[18px]" : "top-[14px]"} text-200`,
              !placeholder && value && "-top-2 text-100",
              !!error && "text-red-100",
            )}
          >
            {label}
          </div>
        )}
        <div className={clsx("truncate", textAlignLeft && "text-left")}>
          {preText && <span className="text-black-54">{preText}</span>}
          <span className={classNameSelected}>
            {items.find(({ id }) => id === value)?.title || placeholder}
          </span>
        </div>
        <div
          className={clsx(
            (!showClearButton || !value) && "duration-300",
            isExpand && "rotate-180",
          )}
        >
          {showClearButton && value && !isExpand ? (
            <div className="text-black-100" onClick={handleClearValue}>
              <TrashXSvg />
            </div>
          ) : chevronType === "smallBlue" ? (
            <div className="text-primary-300">
              <ChevronSmallDownSvg />
            </div>
          ) : (
            <div className="text-black-54">
              <ChevronDownSvg />
            </div>
          )}
        </div>
      </button>
      {error && <p className="pl-4 pt-1 text-100 text-red-100">{error}</p>}
      {isExpand && (
        <div
          className={clsx(
            "absolute z-10 -mt-px w-full bg-white-100 shadow-sm",
            !expandToTop ? "rounded-b-lg" : "bottom-12 rounded-t-lg",
            !isExpand && "hidden",
            classNameExpandedContainer,
          )}
        >
          <ul className="py-2 text-black-54">
            {!items?.length && noItemsLabel ? (
              <li className="pointer-events-none flex h-12 items-center px-6">
                {noItemsLabel}
              </li>
            ) : (
              items.map(({ id, title }) => (
                <li
                  key={id}
                  className="flex h-12 cursor-pointer items-center px-6 hover:bg-black-8"
                  onClick={() => handleSelect(id)}
                >
                  {title}
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};
